/**
 * Phase A6 — Admin email + password signin.
 *
 * Replaces the magic-link version wholesale. Handles the same A2/A5
 * failure cases as /employer-signin: invalid credentials, locked out,
 * password_not_set, temp_password_expired, and forced password change
 * redirect (admin seeds land on /set-password?context=forced).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { adminMe, adminSignin } from "../lib/admin";
import { track } from "../lib/analytics";
import { isValidEmail } from "../lib/dataHygiene";

import "./Admin.css";

const ERR_COPY = {
    invalid_credentials: "That email and password combination didn't match.",
    password_not_set: (
        "Your account doesn't have a password yet. Check your email for the "
        + "temporary password we sent you, or ask another admin to resend it."
    ),
    temp_password_expired: (
        "Your temporary password has expired. Ask another admin to issue a "
        + "fresh one."
    ),
    locked_out: "Too many attempts. Try again in a minute.",
    invalid_email: "That email doesn't look right — mind checking it?",
    network: "Couldn't reach the server. Try again in a moment.",
};

export default function AdminLogin() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(params.get("err") || null);
    const justRegistered = params.get("just_registered") === "1";

    useEffect(() => {
        document.title = "Laguna · admin sign-in";
        track("admin_login_view", {});
        (async () => {
            try {
                await adminMe();
                navigate("/admin/submissions");
            } catch (_) { /* unauth, stay here */ }
        })();
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password || submitting) return;
        if (!isValidEmail(email.trim())) {
            setErr("invalid_email");
            return;
        }
        setSubmitting(true);
        setErr(null);
        try {
            const res = await adminSignin({
                email: email.trim(),
                password,
            });
            track("admin_signin_ok", { forced: res.redirect_to?.includes("forced") });
            window.location.href = res.redirect_to || "/admin/submissions";
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 401 && detail === "password_not_set") {
                setErr("password_not_set");
            } else if (status === 401 && detail === "temp_password_expired") {
                setErr("temp_password_expired");
            } else if (status === 401) {
                setErr("invalid_credentials");
            } else if (status === 429) {
                setErr("locked_out");
            } else {
                setErr("network");
            }
            track("admin_signin_fail", { status, detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-page" data-testid="admin-login">
            <div className="admin-auth-wrap">
                <div className="admin-auth-card">
                    <div className="admin-auth-eyebrow mono">
                        LAGUNA · ADMIN
                    </div>
                    <h1 className="admin-auth-h1">Sign in</h1>
                    <p className="admin-auth-sub">
                        Email and password. If your account was just seeded,
                        use the temporary password your ops lead shared with
                        you — you'll set a permanent one on the next screen.
                    </p>
                    {justRegistered && (
                        <div
                            className="admin-auth-error"
                            data-testid="admin-login-just-registered"
                            style={{
                                background: "rgba(44, 122, 74, 0.08)",
                                color: "#2c7a4a",
                                borderColor: "#2c7a4a",
                            }}
                        >
                            Account created. Sign in with the password you just chose.
                        </div>
                    )}
                    <form onSubmit={submit}>
                        <label className="admin-auth-label">
                            Email
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                autoFocus
                                data-testid="admin-login-email"
                                disabled={submitting}
                            />
                        </label>
                        <label className="admin-auth-label">
                            Password
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                data-testid="admin-login-password"
                                disabled={submitting}
                            />
                        </label>
                        {err && (
                            <div className="admin-auth-error"
                                 data-testid="admin-login-error">
                                {ERR_COPY[err] || ERR_COPY.network}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="admin-btn primary admin-auth-submit"
                            disabled={submitting || !email || !password}
                            data-testid="admin-login-submit"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </button>
                    </form>
                    <p
                        className="admin-auth-sub"
                        style={{ marginTop: "1.25rem", fontSize: "0.85rem" }}
                    >
                        Need an account?{" "}
                        <Link to="/admin/signup" data-testid="admin-login-to-signup">
                            Create one
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
